import { AccountHeader, useApeironSsoCookieHandler } from '@apeiron/auth'
import {
  AdeptusSuccessPopup,
  ApeironCookieConsent,
  EnoughRoleOnly,
  Footer,
  GoogleTagManagerScript,
  noForwardProps,
  Snackbar,
  useBuildChecker,
  useGoogleTagManager,
  useLanguagePath,
  usePersistUTM
} from '@apeiron/temp'
import { ApeironMessagePopup, usePath } from '@apeiron/ui'
import { Box, styled } from '@mui/material'
import BackgroundJPG from '@public/images/background-battle-demo.jpg?responsive'
import BackgroundRepeatPNG from '@public/images/background-repeat.png'
import BackgroundTopPNG from '@public/images/background-top.png'
import PrimaryNavBar from '@src/components/navigationBar/primary/PrimaryNavBar'
import SeconrdaryNavBar from '@src/components/navigationBar/secondary/SecondaryNavBar'
import OgData from '@src/components/ogData/OgData'
import ApeironBindWalletPopup from '@src/components/share/apeiron/ApeironBindWalletPopup'
import ApeironErrorBoundary from '@src/components/share/apeiron/ApeironErrorBoundary'
import ApeironNoNetworkSnackbar from '@src/components/share/apeiron/ApeironNoNetworkSnackbar'
import ApeironPageExplainationBox from '@src/components/share/apeiron/ApeironPageExplainationBox'
import CheckMaintenance from '@src/components/share/generic/CheckMaintenance'
import NextHead from '@src/components/share/next/Head'
import GeetestScript from '@src/components/share/next/script/GeetestScript'
import NewRelicScript from '@src/components/share/next/script/NewRelicScript'
import StructuredData from '@src/components/structuredData/SharedStructuredData'
import WalletWarningPopup from '@src/components/wallet/WalletWarningPopup'
import ENV_CONFIG from '@src/config'
import { NAV_BAR_CONFIG } from '@src/constants/layout'
import { WhiteList } from '@src/constants/page'
import { ROLE_ACCESS } from '@src/constants/route'
import useApeironWindowInjection from '@src/hooks/common/ui/useApeironWindowInjection'
import { useRouter } from 'next/router'
import NextNProgress from 'nextjs-progressbar'
import { FC, ReactNode } from 'react'

const Container = styled(Box, noForwardProps)<MaxWidthProps>`
  align-items: center;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: ${NAV_BAR_CONFIG.primary.height}px;
  ${props =>
    props.__useMaxWidth
      ? `
        background: url(${BackgroundJPG.src});
        background-repeat: repeat;
        background-size: cover;
      `
      : `
        background: url(${BackgroundTopPNG.src}) top center repeat-x, url(${BackgroundRepeatPNG.src}) top center repeat;
      `}
`

const ContentContainer = styled(Box, noForwardProps)<MaxWidthProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-x: clip;
  position: relative;
  width: 100%;
  ${props =>
    props.__useMaxWidth
      ? `
        box-shadow: 0px -18px 20px #111;
        margin: 0 auto;
        max-width: 1920px;
      `
      : ''}
`

const Layout: FC<Props> = (props: Props) => {
  const { children } = props

  const { noFullWidth, noHeader, noFooter, noNavBar } = usePath(WhiteList)

  const { gtmAcceptConsent } = useGoogleTagManager()

  const router = useRouter()

  useApeironWindowInjection()

  useBuildChecker()

  useLanguagePath(router)

  usePersistUTM()

  useApeironSsoCookieHandler()

  return (
    <>
      <NextNProgress color='#ffb752' height={1} showOnShallow={false} />
      <NextHead />
      <OgData />
      <Container __useMaxWidth={noFullWidth}>
        {!noHeader && <AccountHeader />}
        <SeconrdaryNavBar />
        <ContentContainer __useMaxWidth={noFullWidth}>
          <GeetestScript />
          <GoogleTagManagerScript gtmId={ENV_CONFIG.ID.GOOGLE_TAG_MANAGER} />
          <NewRelicScript />
          <EnoughRoleOnly restriction={ROLE_ACCESS}>
            <CheckMaintenance>
              <ApeironErrorBoundary>
                <StructuredData />
                {children}
                <ApeironCookieConsent onAccept={gtmAcceptConsent} />
              </ApeironErrorBoundary>
            </CheckMaintenance>
          </EnoughRoleOnly>
        </ContentContainer>
      </Container>
      {!noNavBar && <PrimaryNavBar />}
      {!noFooter && (
        <Footer
          mediaKitUrl={ENV_CONFIG.EXTERNAL_URL.APEIRON.MEDIA_KIT}
          privacyUrl={ENV_CONFIG.EXTERNAL_URL.APEIRON.PRIVACY}
          termsUrl={ENV_CONFIG.EXTERNAL_URL.APEIRON.TERMS}
        />
      )}
      <ApeironPageExplainationBox />
      <AdeptusSuccessPopup />
      <WalletWarningPopup />
      <ApeironMessagePopup />
      <Snackbar />
      <ApeironNoNetworkSnackbar />
      <ApeironBindWalletPopup />
    </>
  )
}

type MaxWidthProps = {
  __useMaxWidth?: boolean
}

type Props = {
  children: ReactNode
}

export default Layout
